import { useMemo, useState } from 'react';
import CreateButton from 'components/buttons/CreateButton';
import Card from 'components/card';
import CustomExportModal from 'components/modals/CustomExportModal';
import { downloadAllCustomers, downloadCustomersAnalytics, downloadCustomersConversions } from 'interfaces/customer';
import { downloadAllProducts } from 'interfaces/products';
import { downloadAllSuppliers } from 'interfaces/supplier';
import { exportOrders } from 'interfaces/order';

const ExportCard = (props: {
    name: string;
    description: string;
    commingSoon?: boolean;
    onClick?: () => void;
}) => {
    const { name, description, commingSoon = false, onClick } = props;

    return (
        <Card extra={'relative w-full mt-4 px-2 py-6 border'}>
            {/* Badge */}
            <div className="absolute top-3 right-3 bg-green-600/80 text-white text-xs px-2 py-1 rounded-lg">
                {'XLS'}
            </div>
            {/* Header */}
            <div className="w-full px-1">
                <h4 className="text-xl font-medium">{name}</h4>
                <p className="mt-2 text-xs">{description}</p>
            </div>
            <div className="mt-4 flex justify-center">
                {commingSoon ? (
                    <button
                        className={`text-sm px-5 h-10 linear flex items-center justify-center rounded-xl bg-[#518dd1] py-3 font-medium text-white w-full cursor-not-allowed`}
                    >
                        Bientôt disponible
                    </button>
                ) : (
                    <CreateButton
                        value="Exporter"
                        extra="text-sm px-5 h-10 w-full mb-0 bg-toola-500"
                        onClick={onClick}
                    />

                )}
            </div>
        </Card>
    );
};

const ExportList = () => {
    // Each export card mirrors the backend permission gating its own route —
    // same catalog as the Users screen's permissions picker. Owner and 'all'
    // always see everything, matching every other permission check in the app.
    const hasPermission = useMemo(() => {
        const storedPermissions = JSON.parse(localStorage.getItem('permissions') || '[]') as string[];
        const owner = !localStorage.getItem('ownerId');
        return (name: string) => owner || storedPermissions.includes('all') || storedPermissions.includes(name);
    }, []);

    const canExportProducts = hasPermission('read:products');
    const canExportCustomers = hasPermission('read-assigned:customers');
    const canExportOrders = hasPermission('read-assigned:orders');
    const canExportSuppliers = hasPermission('read-assigned:suppliers');
    const canCreateCustomExport = hasPermission('createManageExport');

    const [isCustomExportOpen, setIsCustomExportOpen] = useState(false);

    const downloadExportAllCustomers = async () => {
        try {
            const result = await downloadAllCustomers();
            if (!result) return;
        } catch (error) {
            console.error('Erreur export clients:', error);
        }
    };

    const downloadExportAllProducts = async () => {
        try {
            const result = await downloadAllProducts();
            if (!result) return;
        } catch (error) {
            console.error('Erreur export produits:', error);
        }
    };

    const downloadExportCustomersAnalytics = async () => {
        try {
            const result = await downloadCustomersAnalytics();
            if (!result) return;
        } catch (error) {
            console.error('Erreur export analytics clients:', error);
        }
    };

    const downloadExportConversions = async () => {
        try {
            const result = await downloadCustomersConversions();
            if (!result) return;
        } catch (error) {
            console.error('Erreur export conversions:', error);
        }
    };

    const downloadExportAllOrders = async () => {
        try {
            const blob = await exportOrders(null);
            if (!blob) return;
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'export-commandes.csv';
            link.click();
        } catch (error) {
            console.error('Erreur export commandes:', error);
        }
    };

    const downloadExportAllSuppliers = async () => {
        try {
            const result = await downloadAllSuppliers();
            if (!result) return;
        } catch (error) {
            console.error('Erreur export fournisseurs:', error);
        }
    };

    return (
        <div className="mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {canExportCustomers && (
                    <ExportCard
                        name="Achats par client"
                        description="Quantité et total HT de produits commandés par un client sur une période."
                        //onClick={downloadExportByCustomer}
                        commingSoon={true}
                    />
                )}
                {canExportProducts && (
                    <ExportCard
                        name="Mes produits"
                        description="Exportez la totalité de vos produits sur un fichier Excel."
                        onClick={downloadExportAllProducts}
                    />
                )}
                {canExportCustomers && (
                    <ExportCard
                        name="Mes clients"
                        description="Exportez la totalité de vos clients sur un fichier Excel."
                        onClick={downloadExportAllCustomers}
                    />
                )}
                {canExportCustomers && (
                    <ExportCard
                        name="Statistiques clients"
                        description="CA total, panier moyen, nombre de commandes et dates par client."
                        onClick={downloadExportCustomersAnalytics}
                    />
                )}
                {canExportCustomers && (
                    <ExportCard
                        name="Conversions prospects → clients"
                        description="Nom, tags, date de conversion et délai pour chaque prospect devenu client."
                        onClick={downloadExportConversions}
                    />
                )}
                {canExportOrders && (
                    <ExportCard
                        name="Mes commandes"
                        description="Exportez la totalité de vos commandes sur un fichier CSV."
                        onClick={downloadExportAllOrders}
                    />
                )}
                {canExportSuppliers && (
                    <ExportCard
                        name="Mes fournisseurs"
                        description="Exportez la totalité de vos fournisseurs sur un fichier Excel."
                        onClick={downloadExportAllSuppliers}
                    />
                )}
                {canCreateCustomExport && (
                    <ExportCard
                        name="Export personnalisé"
                        description="Choisissez l'entité et les colonnes à inclure dans votre propre fichier CSV."
                        onClick={() => setIsCustomExportOpen(true)}
                    />
                )}
            </div>
            {!canExportProducts && !canExportCustomers && !canExportOrders && !canExportSuppliers && !canCreateCustomExport && (
                <p className="mt-6 text-sm text-gray-500">
                    Aucun export disponible avec vos permissions actuelles. Contactez votre administrateur.
                </p>
            )}
            <CustomExportModal isOpen={isCustomExportOpen} onClose={() => setIsCustomExportOpen(false)} />
        </div>
    );
};

export default ExportList;
